
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Target } from "lucide-react";
import { InventoryMatch, ParsedColorInfo } from "@/hooks/color-check/types";

interface BestMatchSectionProps {
  bestMatch: InventoryMatch;
  parsedInfo: ParsedColorInfo;
}

const BestMatchSection = ({ bestMatch, parsedInfo }: BestMatchSectionProps) => {
  const getMatchBadge = () => {
    if (bestMatch.colorMatch && bestMatch.sizeMatch) {
      return (
        <Badge className="ml-2 bg-green-100 text-green-800 text-xs">
          PERFECTE MATCH
        </Badge>
      );
    } else if (bestMatch.colorMatch) {
      return (
        <Badge className="ml-2 bg-blue-100 text-blue-800 text-xs">
          JUISTE KLEUR
        </Badge>
      );
    } else if (bestMatch.sizeMatch) {
      return (
        <Badge className="ml-2 bg-orange-100 text-orange-800 text-xs">
          JUISTE MAAT
        </Badge>
      );
    } else {
      return (
        <Badge className="ml-2 bg-yellow-100 text-yellow-800 text-xs">
          ALTERNATIEF
        </Badge>
      );
    }
  };

  const getContainerClass = () => {
    if (bestMatch.colorMatch && bestMatch.sizeMatch) {
      return 'bg-green-50 border-green-200';
    } else if (bestMatch.colorMatch) {
      return 'bg-blue-50 border-blue-200';
    } else if (bestMatch.sizeMatch) {
      return 'bg-orange-50 border-orange-200';
    } else {
      return 'bg-yellow-50 border-yellow-200';
    }
  };

  const getTitleClass = () => {
    if (bestMatch.colorMatch && bestMatch.sizeMatch) {
      return 'text-green-800';
    } else if (bestMatch.colorMatch) {
      return 'text-blue-800';
    } else if (bestMatch.sizeMatch) {
      return 'text-orange-800';
    } else {
      return 'text-yellow-800';
    }
  };

  const getScoreBadgeClass = () => {
    if (bestMatch.colorMatch && bestMatch.sizeMatch) {
      return 'bg-green-600 text-white';
    } else if (bestMatch.colorMatch) {
      return 'bg-blue-600 text-white';
    } else if (bestMatch.sizeMatch) {
      return 'bg-orange-600 text-white';
    } else {
      return 'bg-yellow-600 text-white';
    }
  };

  return (
    <div>
      <h4 className="font-medium text-sm mb-2 flex items-center">
        <Target className="mr-1 h-4 w-4 text-green-500" />
        Beste Match Gevonden
        {getMatchBadge()}
      </h4>
      <div className={`p-4 rounded-lg border ${getContainerClass()}`}>
        <div className="flex items-center justify-between mb-2">
          <h5 className={`font-semibold ${getTitleClass()}`}>
            {bestMatch.product_naam}
          </h5>
          <Badge className={getScoreBadgeClass()}>
            {Math.round(bestMatch.matchScore)}% match
          </Badge>
        </div>
        
        {/* Color Information Highlight */}
        <div className={`mb-2 p-2 rounded ${
          bestMatch.colorMatch ? 'bg-green-100' : 'bg-red-100'
        }`}>
          <div className="flex items-center justify-between text-sm">
            <span className={bestMatch.colorMatch ? 'text-green-700' : 'text-red-700'}>
              <strong>Kleur:</strong> {bestMatch.colorMatch ? 'Juiste kleur' : 'Verkeerde kleur'}
            </span>
            <span className="text-gray-600">
              Gevraagd: {parsedInfo.lightDarkIndicator === 'dark' ? 'Donker (ZX)' : 
                        parsedInfo.lightDarkIndicator === 'light' ? 'Licht (WN)' : 'Onbekend'}
            </span>
          </div>
          {!bestMatch.colorMatch && (
            <p className="text-xs text-red-600 mt-1">
              ❌ Dit is een andere kleur dan gevraagd
            </p>
          )}
        </div>
        
        {/* Size Information */}
        {parsedInfo.size && (
          <div className={`mb-2 p-2 rounded ${bestMatch.sizeMatch ? 'bg-green-100' : 'bg-orange-100'}`}>
            <div className="flex items-center justify-between text-sm">
              <span className={bestMatch.sizeMatch ? 'text-green-700' : 'text-orange-700'}>
                <strong>Maat:</strong> {bestMatch.eenheid || 'Onbekend'}
              </span>
              <span className={bestMatch.sizeMatch ? 'text-green-600' : 'text-orange-600'}>
                Gevraagd: {parsedInfo.size}
              </span>
            </div>
            {!bestMatch.sizeMatch && (
              <p className="text-xs text-orange-600 mt-1">
                ⚠️ Dit is een andere maat dan gevraagd
              </p>
            )}
          </div>
        )}
        
        <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
          <div><strong>Product ID:</strong> {bestMatch.product_id}</div>
          <div><strong>Scancode:</strong> {bestMatch.bc || 'N/A'}</div>
          <div><strong>Voorraad:</strong> {bestMatch.voorraad} {bestMatch.eenheid}</div>
          <div><strong>Locatie:</strong> {bestMatch.locatie || 'Onbekend'}</div>
          <div><strong>Magazijn:</strong> {bestMatch.magazijn} {bestMatch.magazijn === 2 ? '(Goes)' : ''}</div>
          <div><strong>Match Type:</strong> {bestMatch.matchType || 'unknown'}</div>
        </div>
        <p className={`text-sm mt-2 font-medium ${getTitleClass().replace('800', '600')}`}>
          {bestMatch.matchReason}
        </p>
      </div>
    </div>
  );
};

export default BestMatchSection;
